import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { inject, Injectable, isDevMode, signal } from '@angular/core';
import { ConsentFormDialogComponent } from '@consent/components/consent-form-dialog/consent-form-dialog.component';
import { CONSENT_API } from '@consent/constants/consent.constants';
import {
  ConsentAcceptRequest,
  ConsentAcceptResponse,
  ConsentDialogResult,
  ConsentFormData,
  ConsentFormResponse,
  ConsentStatusResponse
} from '@consent/models/consent-form.model';
import { TOASTER_TYPE } from '@constants/app.enums';
import { ConfigService } from '@services/config.service';
import { ProfileService } from '@services/profile.service';
import { UserStateService } from '@services/user-state.service';
import { ToasterService } from '@services/toaster.service';
import { environment } from '@environment/environment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  catchError,
  defaultIfEmpty,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs';

/**
 * Consent Management — fetches the latest *published* consent form for the
 * configured organisation and drives the consent dialog. Self-contained: it
 * opens its own dialog via PrimeNG's {@link DialogService}.
 *
 * Flow: check {@link getConsentStatus} first — its `data.status` boolean says
 * whether the user still owes consent (covers first-time, expiry and version
 * reconsent). Only when consent is required do we fetch the latest published
 * form and open the dialog; acceptance is recorded via the accept API.
 *
 * Enforcement: the authenticated shell ({@link PagesComponent}) triggers
 * {@link ensureGranted} once it has mounted, so the dialog appears OVER the
 * landed page (the dashboard) — not suspended over the login screen. Until
 * {@link granted} flips true, the shell holds back all background API/socket
 * work (it gates that on the signal) and the `ConsentGuard` (canActivateChild)
 * redirects any deeper navigation back to the dashboard. So a purely visual
 * modal can't be bypassed: nothing meaningful runs until consent is granted,
 * yet the user still sees the app behind the (mandatory) dialog.
 */
@Injectable({
  providedIn: 'root'
})
export class ConsentFormService {
  private readonly http = inject(HttpClient);
  private readonly dialogService = inject(DialogService);
  private readonly profileService = inject(ProfileService);
  private readonly toaster = inject(ToasterService);
  private readonly userStateService = inject(UserStateService);
  private readonly config = inject(ConfigService);

  /**
   * Max time (ms) to wait for the authenticated profile's `uuid` to load before
   * giving up. Without this, a profile fetch that never resolves a uuid would
   * leave {@link ensureGranted} hanging forever, blocking the gate guard
   * indefinitely. On timeout we fail open (allow) but leave {@link granted}
   * false so the next navigation re-checks (see {@link ensureSessionProfileUuid}).
   */
  private static readonly PROFILE_WAIT_MS = 15_000;

  /**
   * Single shared in-flight consent resolution. Concurrent guard evaluations
   * (rapid navigation / redirect storms) attach to the SAME run so they share
   * one dialog instead of opening several. `refCount: false` keeps the flow
   * alive even if the triggering navigation is cancelled mid-dialog; it self-
   * completes (the dialog closing or status resolving), so nothing leaks.
   */
  private inFlight$?: Observable<boolean>;

  /** Live ref to the open consent dialog, so session teardown can close it. */
  private openDialogRef?: DynamicDialogRef;

  private readonly _resolved = signal(false);

  /**
   * Flips true once the consent step has fully resolved this session — whether
   * that's "accepted", "already consented", "not configured", or a fail-open
   * error. Read by consumers that must run strictly AFTER the consent step has
   * settled but do not require it to have been *accepted*.
   */
  readonly resolved = this._resolved.asReadonly();

  private readonly _granted = signal(false);

  /**
   * Flips true ONLY when consent is genuinely satisfied: the backend reported
   * the user is fully consented (no prompt needed), or the user explicitly
   * accepted in the dialog. Distinct from {@link resolved}, which also covers
   * fail-open/error outcomes. Downstream consents that legally depend on this
   * one (the SurePass/DigiLocker KYC consent) gate on THIS signal.
   */
  readonly granted = this._granted.asReadonly();

  private readonly _required = signal(false);

  /**
   * Flips true once the backend confirms this user still owes consent (so the
   * dialog is being shown). Used by the consent route guard to block navigation
   * to deeper routes until consent is granted — WITHOUT penalising already-
   * consented users (for whom this stays false), and without false-blocking
   * during the brief status-resolution window (it is false until the status
   * call returns). Pairs with {@link granted}: a user is "blocked" only while
   * `required() && !granted()`.
   */
  readonly required = this._required.asReadonly();

  /**
   * Resolves consent for the current session and reports whether the gate may
   * proceed. Awaited by `ConsentGuard`. While a required consent dialog is open
   * the returned stream stays PENDING — which is exactly what suspends route
   * activation (no shell, no background work) until the user accepts.
   *
   * Returns `true` once consent is granted, not required, not configured, or on
   * a fail-open error (logged). It never emits `false`: a hard "deny" is modelled
   * as the still-pending stream, not a value (returning `false` from `canMatch`
   * would let Angular fall through to the wildcard route and redirect-loop).
   */
  ensureGranted(): Observable<boolean> {
    // Already granted this session → let navigation through immediately.
    if (this._granted()) {
      return of(true);
    }
    // A resolution is already running → share it (one dialog for many callers).
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.resolveConsent().pipe(
      finalize(() => {
        this.inFlight$ = undefined;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.inFlight$;
  }

  /**
   * Clears ALL consent session state. Called by the shared session teardown on
   * logout / 401 / token-theft / 440 so that (a) any open consent dialog is
   * closed immediately (it must not linger over the login screen), and (b) the
   * root singleton's state is reset — without this, the next login (SPA nav, no
   * reload) would see `granted` still true and skip a required consent prompt.
   */
  resetSession(): void {
    this.inFlight$ = undefined;
    this._resolved.set(false);
    this._granted.set(false);
    this._required.set(false);
    this.openDialogRef?.close();
    this.openDialogRef = undefined;
  }

  /** GET the latest published consent form for an organisation. */
  getLatestPublishedForm(orgId: string): Observable<ConsentFormResponse> {
    const url = `${CONSENT_API.FORM_BY_ORG}/${encodeURIComponent(orgId)}/latest-published`;
    return this.http.get<ConsentFormResponse>(url);
  }

  /**
   * GET the current consent status for the authenticated user. The backend does
   * the decision-making (validity, expiry, version reconsent) and returns a
   * single `data.status` boolean — the client just obeys it via
   * {@link requiresConsent}.
   *
   * Auth is the `X-API-Key` header (the same public consent-service key used to
   * record acceptance), never a bearer. `formCode` is only needed when the org
   * publishes more than one form; omitted for single-form orgs.
   */
  getConsentStatus(
    externalUserId: string,
    formCode?: string
  ): Observable<ConsentStatusResponse> {
    let params = new HttpParams().set('external_user_id', externalUserId);
    if (formCode) {
      params = params.set('form_code', formCode);
    }
    const headers = new HttpHeaders({
      'X-API-Key': this.config.acceptConsentKey
    });
    return this.http.get<ConsentStatusResponse>(CONSENT_API.CONSENT_STATUS, {
      headers,
      params
    });
  }

  /**
   * The core resolution pipeline behind {@link ensureGranted}: wait for the
   * authenticated uuid, ask the backend whether consent is owed, and only if so
   * fetch the latest published form and open the (non-dismissable) dialog.
   *
   * Status-first (NOT forkJoin): a user already consented to the active version
   * must trigger neither a form fetch nor a dialog. `switchMap` runs the steps
   * in order and skips the form fetch when {@link requiresConsent} is false.
   *
   * Fail-open philosophy: any runtime failure (uuid wait timing out, status or
   * form fetch erroring) is logged and treated as "allow" so a backend outage
   * never locks users out of the entire app — but {@link granted} is left false
   * so the next navigation re-checks once the backend recovers. (A client gate
   * is defense-in-UI, not the security boundary — see class doc.)
   */
  private resolveConsent(): Observable<boolean> {
    // The consent API is a separate service. Without its base URL, the org id,
    // or the API key, consent cannot be resolved — treat as "not configured for
    // this environment" and allow (nothing to enforce).
    if (
      !environment.consentFormUrl ||
      !this.config.orgId ||
      !this.config.acceptConsentKey
    ) {
      if (isDevMode()) {
        console.warn(
          '[Consent] Skipped: consent environment is not fully configured.'
        );
      }
      this.markGranted();
      return of(true);
    }

    const orgId = this.config.orgId;

    return this.ensureSessionProfileUuid().pipe(
      switchMap((externalUserId) => {
        // Couldn't resolve the user → fail open (don't trap), leave `granted`
        // false so a later navigation re-checks once the profile is available.
        if (!externalUserId) {
          this._resolved.set(true);
          return of(true);
        }
        return this.getConsentStatus(externalUserId).pipe(
          switchMap((status) => {
            // Already fully consented to the active version → grant immediately:
            // no form fetch, no dialog.
            if (!this.requiresConsent(status)) {
              this.markGranted();
              return of(true);
            }
            // User owes consent → the dialog will be shown. Mark required so the
            // route guard can hold deeper navigation on the dashboard meanwhile.
            this._required.set(true);
            return this.getLatestPublishedForm(orgId).pipe(
              switchMap((response) => {
                const form = response?.data;
                if (response?.status === 'success' && form?.custom_body) {
                  return this.openConsentDialog(form);
                }
                // Published form unavailable/malformed: fail open (don't trap the
                // user), but leave `granted` false so a later navigation re-checks.
                this._resolved.set(true);
                return of(true);
              })
            );
          })
        );
      }),
      catchError((error) => {
        console.error(
          '[Consent] Failed to resolve consent status / load form',
          error
        );
        this._resolved.set(true);
        return of(true);
      })
    );
  }

  /**
   * Resolves the authenticated user's uuid for the status check, in order of
   * preference:
   *   1. the already-hydrated session profile (no network);
   *   2. the uuid the login/OTP-verify response persisted to local storage;
   *   3. a `getProfile()` fetch as a last resort.
   *
   * Step 2 is what makes consent enforceable. When consent is *required* the
   * backend gates the `/user` profile endpoint behind consent too, so a
   * `getProfile()` call here returns 451 — the uuid never arrives, the gate
   * fails open, and the dialog is silently skipped (a bootstrap deadlock: the
   * consent check depended on the very endpoint consent blocks). Reading the
   * uuid the login already stored breaks that cycle WITHOUT touching the
   * protected endpoint, so the status check can run and the dialog can open.
   *
   * Resolves to `null` (→ fail-open) on error/timeout.
   */
  private ensureSessionProfileUuid(): Observable<string | null> {
    const cached = this.resolveUuidFromCache();
    if (cached) {
      return of(cached);
    }

    return this.profileService.getProfile().pipe(
      map((response) => response?.data?.uuid ?? null),
      take(1),
      timeout({ first: ConsentFormService.PROFILE_WAIT_MS }),
      catchError((error) => {
        console.error(
          '[Consent] Failed to load profile for consent check',
          error
        );
        return of(null);
      })
    );
  }

  /**
   * The authenticated user's uuid from already-resolved sources, WITHOUT a
   * network call: the hydrated session profile first, then the uuid the login
   * response persisted to storage (see {@link AuthService.persistLoginData}).
   * Returns `null` when neither is available.
   *
   * This is the single source of truth for the consent uuid. When consent is
   * required the backend gates the `/user` profile endpoint behind consent, so
   * `profileData()` is typically empty during the consent step — the stored
   * login uuid is what lets BOTH the status check and the acceptance record run
   * without hitting that blocked endpoint. Reading it from one place keeps the
   * two paths in lock-step: if the status check could resolve a uuid, so can the
   * accept call (otherwise Accept would silently no-op — the form opens but the
   * acceptance is never recorded).
   */
  private resolveUuidFromCache(): string | null {
    const hydrated = this.profileService.profileData()?.uuid;
    if (hydrated) {
      return hydrated;
    }
    return this.userStateService.getUserData()()?.uuid ?? null;
  }

  /**
   * Whether the backend wants us to prompt for consent. `data.status === true`
   * means the user is fully consented to the active version → skip the form
   * fetch and the dialog. Anything else — `false`, or a missing/garbled payload
   * — is treated as "prompt". Fail-safe: better to over-prompt than to silently
   * skip a legally required consent.
   */
  private requiresConsent(status: ConsentStatusResponse | null): boolean {
    return status?.data?.status !== true;
  }

  /** Marks the consent step both granted and resolved. */
  private markGranted(): void {
    this._granted.set(true);
    this._resolved.set(true);
  }

  /**
   * Opens the consent overlay and resolves once it closes. The PrimeNG dialog
   * panel is stripped of ALL chrome (no header/close, no background, shadow,
   * border or padding) so nothing of the "dialog box" is visible — only the
   * admin-authored `custom_body` document, rendered verbatim in a sandboxed
   * iframe (it provides its own card, copy and action buttons). Consent is
   * mandatory, so backdrop click and Esc are disabled — the dialog closes only
   * on an explicit Accept from inside the iframe, or via session teardown.
   *
   * Returns `true` in every terminal case (the gate fails open), but only marks
   * {@link granted} on an explicit accept.
   */
  private openConsentDialog(form: ConsentFormData): Observable<boolean> {
    const ref = this.dialogService.open(ConsentFormDialogComponent, {
      width: '820px',
      data: { form },
      modal: true,
      showHeader: false,
      // Consent is mandatory: a backdrop click or Esc must NOT dismiss it, or the
      // user would silently skip a legally-required consent. The dialog closes
      // only on an explicit Accept from inside the iframe (or session teardown).
      dismissableMask: false,
      closeOnEscape: false,
      style: { background: 'transparent', boxShadow: 'none', border: 'none' },
      contentStyle: {
        padding: '0',
        background: 'transparent',
        border: 'none',
        overflow: 'hidden'
      }
    });
    this.openDialogRef = ref;

    // Resolves when the dialog closes. The dialog closes with a typed result only
    // when the user explicitly accepts inside the iframe; session teardown closes
    // it with `undefined` (records nothing). Either way the gate resolves.
    return ref.onClose.pipe(
      take(1),
      // Safety net: if the PrimeNG dialog is torn down internally without calling
      // `close()` (error path, hot-reload), `onClose` never emits and this stream
      // would hang forever — keeping the gate pending. Cap the wait.
      timeout({ first: 10 * 60_000 }),
      catchError(() => of(undefined)),
      switchMap((result: ConsentDialogResult | undefined) => {
        if (result?.accepted) {
          return this.recordAcceptance(form).pipe(
            map(() => true),
            defaultIfEmpty(true),
            catchError(() => of(true)),
            tap(() => this.markGranted())
          );
        }
        // Closed without acceptance (teardown / safety timeout): not granted.
        this._resolved.set(true);
        return of(true);
      }),
      finalize(() => {
        this.openDialogRef = undefined;
      })
    );
  }

  /**
   * Records the user's acceptance of the given form version.
   *
   * SECURITY: the request is built entirely from HOST-owned state — `form.id` /
   * `form.version` from our own fetch and the authenticated user's `uuid` from
   * {@link resolveUuidFromCache} (the hydrated profile or the login response we
   * persisted). Nothing from the (untrusted) iframe is trusted here; it only
   * signalled intent. Auth is the `X-API-Key` header, never a bearer.
   */
  private recordAcceptance(
    form: ConsentFormData
  ): Observable<ConsentAcceptResponse> {
    const externalUserId = this.resolveUuidFromCache();
    if (!externalUserId) {
      if (isDevMode()) {
        console.warn(
          '[Consent] Acceptance skipped: no authenticated user uuid.'
        );
      }
      return EMPTY;
    }
    if (!this.config.acceptConsentKey) {
      if (isDevMode()) {
        console.warn(
          '[Consent] Acceptance skipped: acceptConsentKey is not configured (set it in /assets/config/config.json).'
        );
      }
      return EMPTY;
    }

    const body: ConsentAcceptRequest = {
      external_user_id: externalUserId,
      form_id: form.id,
      version: form.version
    };
    const headers = new HttpHeaders({
      'X-API-Key': this.config.acceptConsentKey
    });

    return this.http
      .post<ConsentAcceptResponse>(CONSENT_API.ACCEPT_CONSENT, body, {
        headers
      })
      .pipe(
        tap((response) =>
          this.toaster.display(
            response?.message || 'Consent recorded successfully.',
            TOASTER_TYPE.SUCCESS
          )
        ),
        catchError((error: HttpErrorResponse) => {
          console.error('[Consent] Failed to record acceptance', error);
          // The HTTP error interceptor surfaces a backend-provided `message`. It
          // does NOT for transport failures (network down, CORS, status 0/timeout)
          // or error payloads without a `message` — there the user clicked Accept
          // but gets no feedback that nothing was recorded. Show a fallback ONLY in
          // that gap, so we never double-toast on top of the interceptor.
          if (!error?.error?.message) {
            this.toaster.display(
              'We could not record your consent. Please try again.',
              TOASTER_TYPE.ERROR
            );
          }
          return EMPTY;
        })
      );
  }
}
